// 3PL costs: each booked truckload's negotiated and invoiced charges, the
// carrier invoices typed in from their bills, reconciling, the papers behind
// them, and each truckload's cost split over its orders (#47). Every
// dispatcher's truckloads, for anyone signed in (#42 D-4).
import { type CSSProperties, Fragment, useEffect, useState } from "react";

import {
  downloadCarrierCosts,
  downloadOrderCosts,
  fetchCarrierDocument,
  getAllocation,
  getAssignmentHistory,
  listCarrierCosts,
  listCarriers,
  reconcileCost,
  recordCarrierInvoice,
  removeCarrierDocument,
  reopenCost,
  uploadCarrierDocument,
  voidCarrierInvoice,
} from "../api/client";
import { todayIn } from "../lib/format";
import { type MsgKey, useT } from "../lib/i18n";
import type {
  Allocation,
  AllocationState,
  AssignmentEvent,
  Carrier,
  ChargeKind,
  CostAssignment,
  CostStatus,
  DocumentKind,
} from "../types";

const STATUSES: CostStatus[] = ["open", "invoiced", "reconciled"];
const KINDS: ChargeKind[] = ["linehaul", "accessorial", "adjustment"];
const DOCUMENT_KINDS: DocumentKind[] = ["rate_confirmation", "invoice", "other"];

// Invoices are to the cent.
function usd(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function daysBefore(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function CarrierCostsView({
  onOpenPlan,
}: {
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  const today = todayIn("America/Los_Angeles");
  const [dateFrom, setDateFrom] = useState(daysBefore(today, 6));
  const [dateTo, setDateTo] = useState(today);
  const [carrierId, setCarrierId] = useState("");
  const [status, setStatus] = useState<CostStatus | "">("");
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [rows, setRows] = useState<CostAssignment[] | null>(null);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setRows(await listCarrierCosts({ dateFrom, dateTo, carrierId: carrierId || undefined }));
    } catch (e) {
      setError(t("costs.loadFailed", { msg: message(e) }));
    }
  }

  useEffect(() => {
    listCarriers(true)
      .then(setCarriers)
      .catch(() => setCarriers([]));
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, carrierId]);

  async function exportCsv(download: typeof downloadCarrierCosts) {
    try {
      const { blob, filename } = await download(dateFrom, dateTo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(message(e));
    }
  }

  const shown = (rows ?? []).filter((a) => !status || a.cost_status === status);

  return (
    <section className="starter" style={{ maxWidth: 1280 }}>
      <div className="starter-card" style={{ maxWidth: "none" }}>
        <h1>{t("costs.title")}</h1>
        <p className="starter-desc">{t("costs.desc")}</p>

        <div className="config-row" style={{ alignItems: "flex-end" }}>
          <label className="control">
            <span className="control-label">{t("costs.from")}</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="control">
            <span className="control-label">{t("costs.to")}</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label className="control">
            <span className="control-label">{t("costs.carrier")}</span>
            <select value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
              <option value="">{t("costs.allCarriers")}</option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="control">
            <span className="control-label">{t("costs.statusFilter")}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as CostStatus | "")}>
              <option value="">{t("costs.allStatuses")}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`costs.status.${s}` as MsgKey)}
                </option>
              ))}
            </select>
          </label>
          <button className="btn" onClick={() => setEntering(!entering)} aria-expanded={entering}>
            {t("costs.inv.open")}
          </button>
          <button className="btn" onClick={() => void exportCsv(downloadCarrierCosts)}>
            {t("costs.export")}
          </button>
          <button className="btn" onClick={() => void exportCsv(downloadOrderCosts)}>
            {t("costs.exportOrders")}
          </button>
        </div>

        {error && <p className="upload-status error">{error}</p>}
        {entering && (
          <InvoiceForm
            carriers={carriers}
            truckloads={rows ?? []}
            onDone={() => {
              setEntering(false);
              void load();
            }}
          />
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t("costs.col.date")}</th>
                <th style={th}>{t("costs.col.truck")}</th>
                <th style={th}>{t("costs.col.carrier")}</th>
                <th style={th}>{t("costs.col.orders")}</th>
                <th style={th}>{t("costs.col.negotiated")}</th>
                <th style={th}>{t("costs.col.invoiced")}</th>
                <th style={th}>{t("costs.col.variance")}</th>
                <th style={th}>{t("costs.col.status")}</th>
                <th style={th}>{t("costs.col.allocation")}</th>
                <th style={th}>{t("costs.col.warnings")}</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <Row key={a.id} a={a} onChanged={() => void load()} onOpenPlan={onOpenPlan} />
              ))}
            </tbody>
          </table>
          {rows && shown.length === 0 && <p style={muted}>{t("costs.empty")}</p>}
        </div>
      </div>
    </section>
  );
}

function Row({
  a,
  onChanged,
  onOpenPlan,
}: {
  a: CostAssignment;
  onChanged: () => void;
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const warnings = a.warnings.reduce((n, w) => n + w.order_nos.length, 0);
  return (
    <Fragment>
      <tr aria-label={`${a.plan_date} ${a.truck_id}`}>
        <td style={td}>{a.plan_date}</td>
        <td style={td}>
          {a.truck_id}
          {a.status === "cancelled" && <div style={muted}>{t("costs.cancelled")}</div>}
        </td>
        <td style={td}>{a.carrier_name ?? "—"}</td>
        <td style={td}>{a.order_nos.length}</td>
        <td style={td}>{usd(a.negotiated_usd)}</td>
        <td style={td}>{a.cost_status === "open" ? "—" : usd(a.invoiced_usd)}</td>
        <td style={td}>{a.cost_status === "open" ? "—" : usd(a.variance_usd)}</td>
        <td style={td}>
          <span style={{ ...pill, ...STATUS_STYLE[a.cost_status] }}>
            {t(`costs.status.${a.cost_status}` as MsgKey)}
          </span>
        </td>
        <td style={td}>
          <span style={{ ...pill, ...ALLOCATION_STYLE[a.allocation.state] }}>
            {t(`costs.alloc.state.${a.allocation.state}` as MsgKey)}
          </span>
          {a.allocation.changed && <div style={warn}>{t("costs.alloc.changed")}</div>}
          {a.allocation.state === "provisional" && a.allocation.basis === "estimate" && (
            <div style={warn}>{t("costs.alloc.estimateOnly")}</div>
          )}
        </td>
        <td style={td}>{warnings || ""}</td>
        <td style={td}>
          <button className="btn btn-ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
            {t("costs.details")}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td style={td} colSpan={11}>
            <Details a={a} onChanged={onChanged} onOpenPlan={onOpenPlan} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// A truckload opened: its lines, invoices, what happened to its orders, its
// papers, reconciling and its audit trail.
function Details({
  a,
  onChanged,
  onOpenPlan,
}: {
  a: CostAssignment;
  onChanged: () => void;
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AssignmentEvent[] | null>(null);
  const [docKind, setDocKind] = useState<DocumentKind>("rate_confirmation");
  const [file, setFile] = useState<File | null>(null);
  const invoiceNo = new Map(a.invoices.map((i) => [i.id, i.invoice_no]));

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  function withReason(question: string, fn: (reason: string) => Promise<unknown>) {
    const reason = window.prompt(question)?.trim();
    if (reason) void act(() => fn(reason));
  }

  async function openDocument(id: string) {
    // Opened before the fetch, while the click still counts: browsers block a later popup.
    const tab = window.open("", "_blank");
    try {
      const url = URL.createObjectURL(await fetchCarrierDocument(id));
      if (tab) tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      tab?.close();
      setError(message(e));
    }
  }

  return (
    <section aria-label={`${a.truck_id} ${t("costs.details")}`} style={detailsBox}>
      <div>
        <strong>{t("costs.lines")}</strong>
        <ul style={list}>
          {a.charges.map((c) => (
            <li key={c.id} style={c.voided_at ? { opacity: 0.55 } : undefined}>
              {t(`costs.basis.${c.basis}` as MsgKey)} · {t(`bookings.kind.${c.kind}` as MsgKey)}
              {c.code && ` (${c.code})`} · {usd(c.amount_usd)}
              {c.invoice_id && ` · ${invoiceNo.get(c.invoice_id) ?? ""}`}
              {c.voided_at && ` · ${t("costs.voided", { reason: c.void_reason ?? "" })}`}
            </li>
          ))}
        </ul>
      </div>

      {a.invoices.length > 0 && (
        <div>
          <strong>{t("costs.invoices")}</strong>
          <ul style={list}>
            {a.invoices.map((i) => (
              <li key={i.id} style={i.voided_at ? { opacity: 0.55 } : undefined}>
                {t("costs.invoiceLine", {
                  no: i.invoice_no,
                  date: i.service_date,
                  total: usd(i.total_usd),
                })}
                {i.voided_at ? (
                  ` · ${t("costs.invoiceVoided", { who: i.voided_by ?? "—", reason: i.void_reason ?? "" })}`
                ) : (
                  <button
                    className="btn btn-ghost"
                    disabled={busy || a.cost_status === "reconciled"}
                    onClick={() =>
                      withReason(t("costs.voidReason"), (r) => voidCarrierInvoice(i.id, r))
                    }
                  >
                    {t("costs.voidInvoice")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <span>
          {t("costs.deliveries", { delivered: a.deliveries.delivered, orders: a.deliveries.orders })}
        </span>
        {a.warnings.length > 0 && (
          <ul aria-label={t("costs.col.warnings")} style={list}>
            {a.warnings.map((w) => (
              <li key={w.kind}>
                {t(`costs.warn.${w.kind}` as MsgKey, { orders: w.order_nos.join(", ") })}
              </li>
            ))}
          </ul>
        )}
      </div>

      <CostSplit a={a} />

      <div style={inline}>
        {a.cost_status === "reconciled" ? (
          <>
            <span>
              {t("costs.reconciled", {
                usd: usd(a.reconciled_usd ?? 0),
                who: a.reconciled_by ?? "—",
              })}
            </span>
            <button
              className="btn btn-ghost"
              disabled={busy}
              onClick={() =>
                withReason(t("costs.reopenReason"), (r) => reopenCost(a.id, a.version, r))
              }
            >
              {t("costs.reopen")}
            </button>
          </>
        ) : (
          <button
            className="btn"
            disabled={busy || a.cost_status !== "invoiced"}
            onClick={() => void act(() => reconcileCost(a.id, a.version))}
          >
            {t("costs.reconcile", { usd: usd(a.invoiced_usd) })}
          </button>
        )}
        {a.cost_status === "invoiced" && <span style={muted}>{t("costs.alloc.freezeHint")}</span>}
      </div>

      <div>
        <strong>{t("costs.documents")}</strong>
        {a.documents.length === 0 ? (
          <p style={muted}>{t("costs.noDocuments")}</p>
        ) : (
          <ul style={list}>
            {a.documents.map((d) => (
              <li key={d.id}>
                <button className="btn btn-ghost" onClick={() => void openDocument(d.id)}>
                  {d.file_name}
                </button>
                <span style={muted}>
                  {t(`costs.doc.${d.kind}` as MsgKey)} · {d.uploaded_by}
                </span>
                <button
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() =>
                    window.confirm(t("costs.doc.removeConfirm")) &&
                    void act(() => removeCarrierDocument(a.id, d.id, a.version))
                  }
                >
                  {t("costs.doc.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div style={inline}>
          <select
            aria-label={t("costs.doc.kind")}
            value={docKind}
            onChange={(e) => setDocKind(e.target.value as DocumentKind)}
          >
            {DOCUMENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`costs.doc.${k}` as MsgKey)}
              </option>
            ))}
          </select>
          <input
            type="file"
            aria-label={t("costs.doc.file")}
            accept="image/jpeg,image/png,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            className="btn"
            disabled={!file || busy}
            onClick={() =>
              file && void act(() => uploadCarrierDocument(a.id, a.version, file, docKind))
            }
          >
            {t("costs.doc.upload")}
          </button>
        </div>
      </div>

      <div style={inline}>
        <button
          className="btn btn-ghost"
          onClick={() =>
            getAssignmentHistory(a.id)
              .then(setHistory)
              .catch((e) => setError(message(e)))
          }
        >
          {t("costs.history")}
        </button>
        <button className="btn btn-ghost" onClick={() => onOpenPlan(a.plan_id, a.truck_id)}>
          {t("costs.openPlan")}
        </button>
      </div>
      {history && (
        <ul aria-label={t("costs.history")} style={list}>
          {history.map((e, n) => (
            <li key={n}>
              {new Date(e.at).toLocaleString()} · {e.by_user} · {e.kind.replace(/_/g, " ")}
              {typeof e.detail.reason === "string" && ` — ${e.detail.reason}`}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// A truckload's cost by order: trips, their stops in delivery order and each
// stop's orders, with the total check. Read again whenever the truckload changes.
function CostSplit({ a }: { a: CostAssignment }) {
  const t = useT();
  const [split, setSplit] = useState<Allocation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    getAllocation(a.id)
      .then(setSplit)
      .catch((e) => setError(message(e)));
  }, [a.id, a.version]);

  if (error) return <p className="upload-status error">{error}</p>;
  if (!split) return null;
  const allocated = split.trips
    .flatMap((trip) => trip.stops)
    .reduce((n, s) => n + Math.round(s.amount_usd * 100), 0);
  const percent = (share: number) => `${(share * 100).toFixed(1)}%`;
  return (
    <section aria-label={t("costs.alloc.title")}>
      <strong>{t("costs.alloc.title")}</strong>
      {split.trips.length === 0 ? (
        <>
          <p style={muted}>{t(`costs.alloc.state.${split.state}` as MsgKey)}.</p>
          {split.state === "cancelled" && split.basis !== "estimate" && !!split.total_usd && (
            <p style={warn}>{t("costs.alloc.unallocated", { usd: usd(split.total_usd) })}</p>
          )}
        </>
      ) : (
        <>
          <p style={muted}>
            {t("costs.alloc.splitting", {
              basis: t(`costs.alloc.basis.${split.basis}` as MsgKey),
              usd: usd(split.total_usd ?? 0),
            })}{" "}
            {split.state === "frozen"
              ? t("costs.alloc.frozenBy", {
                  who: split.frozen_by ?? "—",
                  when: new Date(split.frozen_at ?? "").toLocaleString(),
                })
              : t("costs.alloc.provisionalHint")}
          </p>
          {split.changed && <p style={warn}>{t("costs.alloc.changedHint")}</p>}
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t("costs.alloc.stop")}</th>
                <th style={th}>{t("costs.alloc.zip")}</th>
                <th style={th}>{t("costs.alloc.order")}</th>
                <th style={th}>{t("costs.alloc.pallets")}</th>
                <th style={th}>{t("costs.alloc.miles")}</th>
                <th style={th}>{t("costs.alloc.share")}</th>
                <th style={th}>{t("costs.alloc.amount")}</th>
              </tr>
            </thead>
            {split.trips.map((trip, n) => (
              <tbody key={trip.route_id}>
                <tr>
                  <th style={th} colSpan={7}>
                    {t("costs.alloc.trip", { n: n + 1, route: trip.route_id })} ·{" "}
                    {t(trip.open ? "costs.alloc.open" : "costs.alloc.loop")}
                    {trip.method === "sampled" && ` · ${t("costs.alloc.sampled")}`}
                  </th>
                </tr>
                {trip.stops.flatMap((stop, s) =>
                  stop.orders.map((o, k) => (
                    <tr key={`${s}-${o.order_no}`}>
                      <td style={td}>{k === 0 ? s + 1 : ""}</td>
                      <td style={td}>{k === 0 ? stop.zip : ""}</td>
                      <td style={td}>{o.order_no}</td>
                      <td style={td}>{o.pallets}</td>
                      <td style={td}>{k === 0 ? stop.shapley_miles.toFixed(1) : ""}</td>
                      <td style={td}>{percent(o.share)}</td>
                      <td style={td}>{usd(o.amount_usd)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            ))}
          </table>
          <p>
            {t("costs.alloc.total", {
              allocated: usd(allocated / 100),
              total: usd(split.total_usd ?? 0),
            })}
          </p>
        </>
      )}
    </section>
  );
}

interface Line {
  assignmentId: string;
  kind: ChargeKind;
  code: string;
  amount: string;
}

const NEW_LINE: Line = { assignmentId: "", kind: "linehaul", code: "", amount: "" };

// A carrier's invoice typed in from their bill: its lines go on the
// truckloads it covers and must add up to its total.
function InvoiceForm({
  carriers,
  truckloads,
  onDone,
}: {
  carriers: Carrier[];
  truckloads: CostAssignment[];
  onDone: () => void;
}) {
  const t = useT();
  const [carrierId, setCarrierId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [total, setTotal] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>([NEW_LINE]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The carrier's truckloads in the listed dates whose cost is still open to change.
  const choices = truckloads.filter(
    (a) => a.carrier_id === carrierId && a.cost_status !== "reconciled",
  );
  const cents = (s: string) => Math.round(Number(s) * 100);
  const sum = lines.reduce((n, l) => n + cents(l.amount || "0"), 0);
  const ready =
    carrierId &&
    invoiceNo.trim() &&
    serviceDate &&
    total !== "" &&
    lines.every((l) => l.assignmentId && cents(l.amount) !== 0) &&
    sum === cents(total);

  function setLine(n: number, change: Partial<Line>) {
    setLines(lines.map((l, i) => (i === n ? { ...l, ...change } : l)));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await recordCarrierInvoice({
        carrier_id: carrierId,
        invoice_no: invoiceNo.trim(),
        ...(invoiceDate && { invoice_date: invoiceDate }),
        service_date: serviceDate,
        total_usd: Number(total),
        ...(note.trim() && { note: note.trim() }),
        lines: lines.map((l) => ({
          assignment_id: l.assignmentId,
          version: truckloads.find((a) => a.id === l.assignmentId)?.version ?? 0,
          kind: l.kind,
          amount_usd: Number(l.amount),
          ...(l.code.trim() && { code: l.code.trim() }),
        })),
      });
      onDone();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label={t("costs.inv.title")} style={formBox}>
      <strong>{t("costs.inv.title")}</strong>
      <div className="config-row" style={{ alignItems: "flex-end" }}>
        <label className="control">
          <span className="control-label">{t("costs.carrier")}</span>
          <select
            value={carrierId}
            onChange={(e) => {
              setCarrierId(e.target.value);
              setLines([NEW_LINE]);
            }}
          >
            <option value="" />
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="control">
          <span className="control-label">{t("costs.inv.number")}</span>
          <input value={invoiceNo} maxLength={100} onChange={(e) => setInvoiceNo(e.target.value)} />
        </label>
        <label className="control">
          <span className="control-label">{t("costs.inv.date")}</span>
          <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
        </label>
        <label className="control">
          <span className="control-label">{t("costs.inv.serviceDate")}</span>
          <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} />
        </label>
        <label className="control">
          <span className="control-label">{t("costs.inv.total")}</span>
          <input
            type="number"
            step="0.01"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </label>
      </div>
      {lines.map((l, n) => (
        <div key={n} className="config-row" style={{ alignItems: "flex-end" }}>
          <label className="control">
            <span className="control-label">{t("costs.inv.truck")}</span>
            <select
              value={l.assignmentId}
              onChange={(e) => setLine(n, { assignmentId: e.target.value })}
            >
              <option value="">{t("costs.inv.pickTruck")}</option>
              {choices.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.plan_date} · ${a.truck_id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="control">
            <span className="control-label">{t("costs.inv.kind")}</span>
            <select
              value={l.kind}
              onChange={(e) => setLine(n, { kind: e.target.value as ChargeKind })}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`bookings.kind.${k}` as MsgKey)}
                </option>
              ))}
            </select>
          </label>
          <label className="control">
            <span className="control-label">{t("costs.inv.code")}</span>
            <input
              value={l.code}
              maxLength={50}
              onChange={(e) => setLine(n, { code: e.target.value })}
            />
          </label>
          <label className="control">
            <span className="control-label">{t("costs.inv.amount")}</span>
            <input
              type="number"
              step="0.01"
              value={l.amount}
              onChange={(e) => setLine(n, { amount: e.target.value })}
            />
          </label>
          {lines.length > 1 && (
            <button
              className="btn btn-ghost"
              onClick={() => setLines(lines.filter((_, i) => i !== n))}
            >
              {t("costs.inv.removeLine")}
            </button>
          )}
        </div>
      ))}
      <div style={inline}>
        <button className="btn btn-ghost" onClick={() => setLines([...lines, NEW_LINE])}>
          {t("costs.inv.addLine")}
        </button>
        <span style={sum === cents(total || "0") ? muted : { color: "#a4161a", fontSize: 13 }}>
          {t("costs.inv.sum", { sum: usd(sum / 100), total: usd(Number(total || 0)) })}
        </span>
      </div>
      <label className="control">
        <span className="control-label">{t("costs.inv.note")}</span>
        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div style={inline}>
        <button className="btn" disabled={!ready || busy} onClick={() => void save()}>
          {t("costs.inv.save")}
        </button>
        <button className="btn btn-ghost" onClick={onDone}>
          {t("costs.inv.cancel")}
        </button>
      </div>
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

const STATUS_STYLE: Record<CostStatus, CSSProperties> = {
  open: { background: "rgba(234,88,12,0.14)", color: "#c2410c" },
  invoiced: { background: "rgba(37,99,235,0.12)", color: "#1d4ed8" },
  reconciled: { background: "rgba(22,163,74,0.14)", color: "#15803d" },
};
const ALLOCATION_STYLE: Record<AllocationState, CSSProperties> = {
  frozen: { background: "rgba(22,163,74,0.14)", color: "#15803d" },
  provisional: { background: "rgba(37,99,235,0.12)", color: "#1d4ed8" },
  cancelled: { background: "rgba(0,0,0,0.06)", color: "inherit" },
  no_orders: { background: "rgba(0,0,0,0.06)", color: "inherit" },
  no_cost: { background: "rgba(234,88,12,0.14)", color: "#c2410c" },
};
const muted: CSSProperties = { opacity: 0.65, fontSize: 13 };
const warn: CSSProperties = { color: "#c2410c", fontSize: 13 };
const list: CSSProperties = { margin: 0, paddingLeft: 18, lineHeight: 1.8 };
const inline: CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const detailsBox: CSSProperties = { display: "grid", gap: 12, padding: "8px 4px" };
const formBox: CSSProperties = {
  display: "grid",
  gap: 8,
  padding: 10,
  marginTop: 12,
  borderRadius: 8,
  border: "1px dashed rgba(0,0,0,0.2)",
};
const tableWrap: CSSProperties = { overflowX: "auto", marginTop: 12 };
const table: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left",
  padding: "6px 10px",
  borderBottom: "1px solid rgba(0,0,0,0.15)",
};
const td: CSSProperties = { padding: "6px 10px", borderBottom: "1px solid rgba(0,0,0,0.06)" };
const pill: CSSProperties = {
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: 12,
  whiteSpace: "nowrap",
};
